import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import clearDropdownOnChangeYMMD from "@store/Store/Components/RsswYMMD/Handlers/clearDropdownOnChangeYMMD";
import clearYMMD from "@store/Store/Components/RsswYMMD/Handlers/clearYMMD";
import getProductsYMMD, { GetYMMDApiParameter } from "@store/Store/Components/RsswYMMD/Handlers/getProductsYMMD";
import setAllYMMD from "@store/Store/Components/RsswYMMD/Handlers/setAllYMMD";
import setYMMD from "@store/Store/Components/RsswYMMD/Handlers/setYMMD";
import { useProductListYmmdPage, useYMMD } from "@store/Store/Components/RsswYMMD/Hooks/useYMMD";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasHistory;

export const enum fields {
    year = "year",
    make = "make",
    model = "model",
    diameter = "diameter",
}

export interface RsswYMMDContainerStyles {
    wrapperDesktop?: InjectableCss;
    wrapperMobile?: InjectableCss;
    inputsContainer?: GridContainerProps;
    actionsContainer?: GridContainerProps;
    inputSelectGridItem?: GridItemProps;
    yearTextField?: SelectPresentationProps;
    ymmdLabel?: TypographyPresentationProps;
    YMMDSearchButton?: ButtonPresentationProps;
    YMMDResetButton?: ButtonPresentationProps;
    YMMDOpenButton?: ButtonPresentationProps;
    YMMDButtonText?: TypographyPresentationProps;
    YMMDIcon?: IconProps;
    YMMDIconClose?: IconProps;
}

export const rsswYMMDContainerStyles: RsswYMMDContainerStyles = {
    wrapperDesktop: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: ["column", null, null, "row", null],
                            properties: ["flex-direction"],
                        },
                        {
                            values: ["15px", null, null, "0", null],
                            properties: ["margin-top"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    inputsContainer: {
        gap: 0,
        css: css`
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["0 20px", null, null, "0", null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    inputSelectGridItem: {
        width: [12, 12, 12, 3, 3],
    },
    yearTextField: {
        cssOverrides: {
            formField: css`
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            {
                                values: [null, null, null, "15px", null],
                                properties: ["padding-right"],
                            },
                        ],
                        mediaOptions.min,
                    )}
            `,
            inputSelect: css`
                min-height: 45px;
                border: 0;
                border-radius: 2px;
                cursor: pointer;
                font-size: ${TYPOGRAPHY.size.base};
                color: ${COLORS.text.base};

                &:disabled {
                    opacity: 1;
                    background-color: ${COLORS.background.alt};
                    color: ${COLORS.disabled.select};
                }
            `,
            formInputWrapper: css`
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            {
                                values: ["0 0 15px", null, null, "20px 0", null],
                                properties: ["margin"],
                            },
                        ],
                        mediaOptions.min,
                    )}

                select:disabled + ${IconWrapper} {
                    svg {
                        color: ${COLORS.disabled.select};
                    }
                }

                ${IconWrapper} {
                    top: 5px;
                    background: 0 !important;

                    svg {
                        color: ${COLORS.secondary.base};
                    }
                }
            `,
        },
    },
    actionsContainer: {
        css: css`
            display: flex;
            align-items: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: ["15px", null, null, "0", null],
                            properties: ["margin-bottom"],
                        },
                        {
                            values: ["100%", null, null, "auto", null],
                            properties: ["width"],
                        },
                        {
                            values: ["0 20px", null, null, "0", null],
                            properties: ["padding"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    ymmdLabel: {
        color: COLORS.white,
        weight: TYPOGRAPHY.weight.bold,
        size: TYPOGRAPHY.size.base,
        css: css`
            width: 335px;
            margin-right: 55px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: ["none", null, null, "flex", null],
                            properties: ["display"],
                        },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    YMMDSearchButton: {
        variant: "secondary",
        css: css`
            min-width: 200px;
            min-height: 45px;
            border-radius: 3px;

            ${TypographyStyle} {
                font-size: ${TYPOGRAPHY.size.base};
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        {
                            values: [null, null, null, COLORS.black, null],
                            properties: ["border-color"],
                        },
                        {
                            values: [null, null, null, COLORS.black, null],
                            properties: ["background-color"],
                        },
                        {
                            values: ["100%", null, null, "auto", null],
                            properties: ["width"],
                        },
                    ],
                    mediaOptions.min,
                )}

            &:disabled {
                background-color: ${COLORS.secondary.base};
                border-color: ${COLORS.secondary.base};
            }
        `,
    },
    YMMDResetButton: {
        css: css`
            margin-left: 25px;
            color: ${COLORS.white};
            font-size: ${TYPOGRAPHY.size.base};
            text-decoration: underline;

            ${TypographyStyle} {
                font-size: ${TYPOGRAPHY.size.base};
            }

            &:hover,
            &:focus {
                text-decoration: none;
            }
        `,
    },
    YMMDOpenButton: {
        variant: "primary",
        css: css`
            width: 100%;
            min-height: 45px;
            border-radius: 3px;

            span {
                width: 20px;
                height: 8px;

                > svg {
                    width: 20px;
                    height: 20px;
                    transform: translateY(3px);
                }
            }

            &.isOpened {
                display: flex;
                justify-content: space-between;
                align-items: center;
                text-align: left;

                ${TypographyStyle} {
                    flex-basis: 75%;
                }
            }
        `,
    },
    YMMDButtonText: {
        size: TYPOGRAPHY.size.smaller,
        weight: TYPOGRAPHY.weight.bold,
    },
    YMMDIcon: {
        color: COLORS.white,
        css: css`
            margin-left: 7px;
        `,
    },
    YMMDIconClose: {
        color: COLORS.white,
        css: css`
            margin-left: 7px;
        `,
    },
};
const desktopInnerWith = 1200;

const RsswYMMD = ({ id, history }: Props) => {
    const [YMMDIsOpen, setYMMDIsOpen] = useState(false);
    const [apiParameter, setApiParameter] = useState<GetYMMDApiParameter>({});
    const [canSearch, setCanSearch] = useState(false);
    const [ymmdIsLoaded, setYmmdIsLoaded] = useState(false);
    const [usedSearch, setUsedSearch] = useState(false);
    const dispatch = useDispatch();
    const location = useSelector(getLocation);
    const ymmd = useYMMD();
    const productListYmmdPage = useProductListYmmdPage();

    let isDiameterZero = false;

    const deleteEmptyValues = (params: GetYMMDApiParameter) => {
        for (const item in params) {
            if (Object.prototype.hasOwnProperty.call(params, item) && params[item as keyof GetYMMDApiParameter]?.trim() === "") {
                delete params[item as keyof GetYMMDApiParameter];
            }
        }
    }

    const dispatchSetYmmd = (parameters: GetYMMDApiParameter) => {
        delete parameters.diameter; // force ymmd to reset
        dispatch(clearDropdownOnChangeYMMD({ key: fields.diameter }));
        deleteEmptyValues(parameters);
        setCanSearch(false);
        setApiParameter(parameters);
        if (parameters.key === "diameter" && isDiameterZero) {
            dispatch(setYMMD({ key: fields.year }));
            dispatch(setYMMD({ year: parameters.year, key: fields.make }));
            dispatch(setYMMD({ year: parameters.year, make: parameters.make, key: fields.model}));
            isDiameterZero = false;
        }
        dispatch(setYMMD(parameters));
    };

    const dispatchSetAllYmmd = (parameters: GetYMMDApiParameter) => {
        setCanSearch(false);
        setApiParameter(parameters);
        dispatch(setAllYMMD(parameters));
    };

    useEffect(() => {
        const getQueryDiameter = parseQueryStringCaseInsensitive(location.search, {
            year: "",
            make: "",
            model: "",
            diameter: "",
            key: fields.diameter,
        });
        const { year, make, model, diameter } = getQueryDiameter;

        if (usedSearch) {
            // We got here using the search button so the getProductsYMMD has been called already called 
            setUsedSearch(false);
        } else if (year !== "" && make !== "" && model !== "" && diameter !== "") {
            // We got here by navigation so we need to fetch the results as if we did hit the search button
            dispatch(getProductsYMMD(getQueryDiameter));
        }

        if (ymmdIsLoaded && year !== "" && make !== "" && model !== "" && diameter !== "") {
            if (diameter === "0") {
                isDiameterZero = true;
                dispatchSetYmmd({ year, make, model, diameter, key: fields.diameter });
                setCanSearch(false);
            } else {
                dispatchSetAllYmmd({ year, make, model, diameter, key: fields.diameter });
                setCanSearch(true);
            }
        }
    }, [location]);

    useEffect(() => {
        openYMMDForDesktop();
        window.addEventListener("resize", handleWindowResize);

        if (location && location.search) {
            const parsedYmmdQuery = parseQueryStringCaseInsensitive(location.search, {
                year: "",
                make: "",
                model: "",
                diameter: "",
                key: fields.diameter,
            });
            const { year, make, model, diameter } = parsedYmmdQuery;

            if (year !== "" && make !== "" && model !== "" && diameter !== "") {
                if (diameter === "0") {
                    isDiameterZero = true;
                    dispatchSetYmmd({ year, make, model, diameter, key: fields.diameter });
                    setCanSearch(false);
                } else {
                    dispatchSetAllYmmd({ year, make, model, diameter, key: fields.diameter });
                    setCanSearch(true);
                }

                dispatch(getProductsYMMD(parsedYmmdQuery));
            } else {
                const parameters = { ...apiParameter, key: fields.year };
                dispatchSetYmmd(parameters);
            }
        } else {
            const parameters = { ...apiParameter, key: fields.year };
            dispatchSetYmmd(parameters);
        }
        setYmmdIsLoaded(true);
    }, []);

    const handleWindowResize = () => {
        openYMMDForDesktop();
    };

    const openYMMDForDesktop = () => {
        if (window.innerWidth >= desktopInnerWith) {
            setYMMDIsOpen(true);
        }
    };

    const yearChangeHandler = (value: string) => {
        const keyItem = value ? fields.make : fields.year;
        const parameters = { ...apiParameter, year: value, key: keyItem };
        delete parameters.make;
        delete parameters.model;
        delete parameters.diameter;
        dispatch(clearDropdownOnChangeYMMD({ key: keyItem }));
        dispatchSetYmmd(parameters);
        (document?.getElementById("selectMake") as HTMLSelectElement).selectedIndex = 0;
    };

    const makeChangeHandler = (value: string) => {
        const keyItem = value ? fields.model : fields.make;
        const parameters = { ...apiParameter, make: value, key: keyItem };
        delete parameters.model;
        delete parameters.diameter;
        dispatch(clearDropdownOnChangeYMMD({ key: keyItem }));
        dispatchSetYmmd(parameters);
    };

    const modelChangeHandler = (value: string) => {
        const keyItem = value ? fields.diameter : fields.model;
        const parameters = { ...apiParameter, model: value, key: keyItem };
        delete parameters.diameter;
        dispatchSetYmmd(parameters);
        dispatch(clearDropdownOnChangeYMMD({ key: keyItem }));
        (document?.getElementById("selectDiameter") as HTMLSelectElement).selectedIndex = 0;
    };

    const diameterChangeHandler = (value: string) => {
        const parameters = { ...apiParameter, diameter: value, key: fields.diameter };
        setApiParameter(parameters);
        value ? setCanSearch(true) : setCanSearch(false);
    };

    const searchYMMDHandler = () => {
        const { year, make, model, diameter } = apiParameter;
        const ymmdPageUrl = `${productListYmmdPage}?year=${year}&make=${make}&model=${model}&diameter=${diameter}`;
        dispatch(getProductsYMMD(apiParameter));
        if (history && productListYmmdPage) {
            if (window.innerWidth <= 1199) {
                setYMMDIsOpen(false);
            }
            history.push(ymmdPageUrl);
            setUsedSearch(true);
        }
    };

    const resetYMMDHandler = () => {
        dispatch(clearYMMD());
        setApiParameter({});
        setCanSearch(false);
        (document?.getElementById("selectYear") as HTMLSelectElement).selectedIndex = 0;
    };

    const openYMMDHandler = () => {
        setYMMDIsOpen(!YMMDIsOpen);
    };

    const styles = rsswYMMDContainerStyles;

    return (
        <>
            <Hidden above="md">
                <StyledWrapper {...styles.wrapperMobile}>
                    <Button
                        {...styles.YMMDOpenButton}
                        className={`${YMMDIsOpen ? "isOpened" : ""}`}
                        onClick={openYMMDHandler}
                    >
                        <Typography {...styles.YMMDButtonText}>{translate("Search By Vehicle")}</Typography>
                        {YMMDIsOpen && <Icon src={X} {...styles.YMMDIconClose} />}
                    </Button>
                </StyledWrapper>
            </Hidden>
            {YMMDIsOpen && (
                <StyledWrapper {...styles.wrapperDesktop}>
                    <Typography {...styles.ymmdLabel}>{translate("Search by Vehicle")}</Typography>
                    <GridContainer {...styles.inputsContainer}>
                        <GridItem {...styles.inputSelectGridItem}>
                            <Select
                                {...styles.yearTextField}
                                title={translate("Year")}
                                onChange={e => yearChangeHandler(e.currentTarget.value)}
                                id="selectYear"
                                value={apiParameter.year}
                            >
                                <option value="">{translate("Year")}</option>
                                {ymmd.year?.result?.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.inputSelectGridItem}>
                            <Select
                                {...styles.yearTextField}
                                title={translate("Make")}
                                disabled={ymmd.make.result?.length === 0}
                                onChange={e => makeChangeHandler(e.currentTarget.value)}
                                id="selectMake"
                                value={apiParameter.make}
                            >
                                <option value="">{translate("Make")}</option>
                                {ymmd.make?.result?.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.inputSelectGridItem}>
                            <Select
                                {...styles.yearTextField}
                                title={translate("Model")}
                                disabled={ymmd.model.result?.length === 0}
                                onChange={e => modelChangeHandler(e.currentTarget.value)}
                                id="selectModel"
                                value={apiParameter.model}
                            >
                                <option value="">{translate("Model")}</option>
                                {ymmd.model?.result?.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.inputSelectGridItem}>
                            <Select
                                {...styles.yearTextField}
                                title={translate("Diameter")}
                                disabled={ymmd.diameter.result?.length === 0}
                                onChange={e => diameterChangeHandler(e.currentTarget.value)}
                                id="selectDiameter"
                                value={apiParameter.diameter}
                            >
                                <option value="">{translate("Diameter")}</option>
                                {ymmd.diameter?.result?.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                    </GridContainer>
                    <StyledWrapper {...styles.actionsContainer}>
                        <Button {...styles.YMMDSearchButton} onClick={searchYMMDHandler} disabled={!canSearch}>
                            {translate("Search")}
                        </Button>
                        <Clickable {...styles.YMMDResetButton} onClick={resetYMMDHandler}>
                            {translate("Reset")}
                        </Clickable>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const rsswYMMD: WidgetModule = {
    component: withHistory(RsswYMMD),
    definition: {
        group: "Header",
        isSystem: true,
    },
};

export default rsswYMMD;
